import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@cibli/shared';
import { AlertCircle, Calendar, CheckCircle2, Globe, RefreshCw, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  AppInfoBridge,
  environmentFromHostname,
  type NativeAppInfo,
} from '@/bridge/app-info.bridge';

import { useHealthCheck } from '../../../hooks/useHealthCheck';

export function DeploymentInfoCard() {
  const { health, isLoading } = useHealthCheck();
  const [isReloading, setIsReloading] = useState(false);
  // undefined = lecture en cours, null = indisponible (web ou APK trop ancien)
  const [nativeAppInfo, setNativeAppInfo] = useState<NativeAppInfo | null | undefined>(undefined);

  const buildInfo =
    typeof __APP_BUILD_INFO__ !== 'undefined'
      ? __APP_BUILD_INFO__
      : {
          commitMsg: 'Mode Développement local',
          commitDate: 'En cours',
          branch: 'local',
          commitHash: 'dev',
          tag: 'non taggé',
        };

  useEffect(() => {
    let cancelled = false;
    AppInfoBridge.getNativeAppInfo().then(info => {
      if (!cancelled) setNativeAppInfo(info);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleForceReload = async () => {
    setIsReloading(true);
    try {
      // 1. Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      // 2. Clear all cache storage entries
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }
    } catch (e) {
      console.warn('Cache clearing warning:', e);
    }
    // 3. Hard reload via cache busting query and location assign
    setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('_t', Date.now().toString());
      window.location.href = url.toString();
    }, 200);
  };

  const isConnected = !isLoading && Boolean(health && health.status === 'ok');

  return (
    <Card className="border border-emerald-500/40 bg-card text-card-foreground shadow-md">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base font-bold flex items-center gap-2">
          <span>🚀 Déploiement & Version Tablette</span>
        </CardTitle>
        <div className="flex items-center gap-1.5">
          {nativeAppInfo && (
            <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30 whitespace-nowrap">
              APK v{nativeAppInfo.versionName} [{nativeAppInfo.environment}]
            </span>
          )}
          <Badge
            variant={isConnected ? 'default' : 'destructive'}
            className={
              isConnected
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/40'
                : 'bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/40'
            }
          >
            {isConnected ? (
              <span className="flex items-center gap-1.5 text-xs font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" /> API Connectée
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs font-semibold">
                <AlertCircle className="w-3.5 h-3.5" /> API Non joignable
              </span>
            )}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-3 text-sm">
        {/* Deux volets : APK installé vs bundle web déployé */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="bg-muted/60 p-3 rounded-lg border border-border flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Smartphone className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              Application native APK
            </span>
            {nativeAppInfo === undefined ? (
              <span className="text-xs text-muted-foreground">Lecture en cours…</span>
            ) : nativeAppInfo ? (
              <>
                <span className="font-bold text-foreground">
                  Cibli{nativeAppInfo.versionName ? ` v${nativeAppInfo.versionName}` : ''}
                </span>
                <span className="text-[11px] font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  Env: {nativeAppInfo.environment}
                </span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">
                Non disponible (navigateur web ou APK à reconstruire)
              </span>
            )}
          </div>

          <div className="bg-muted/60 p-3 rounded-lg border border-border flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              Version web déployée
            </span>
            <span className="font-bold text-foreground">Release {buildInfo.tag}</span>
            <span className="text-[11px] font-mono font-semibold text-emerald-600 dark:text-emerald-400">
              Env: {environmentFromHostname(window.location.hostname)}
            </span>
            <span className="text-[11px] font-mono text-blue-600 dark:text-blue-400 break-all">
              Branche: {buildInfo.branch}
            </span>
          </div>
        </div>

        {/* Dernier commit déployé + date de build + serveur API */}
        <div className="flex flex-col gap-1.5 bg-muted/60 p-3 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              Dernier commit déployé :
            </span>
            <span className="font-mono text-xs font-bold text-emerald-600 dark:text-emerald-400">
              #{buildInfo.commitHash}
            </span>
          </div>
          <p className="text-sm font-semibold text-foreground leading-snug break-words">
            "{buildInfo.commitMsg}"
          </p>
          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 pt-1.5 border-t border-border/60 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Compilé le : {buildInfo.commitDate}
            </span>
            {health?.releaseName && (
              <span className="font-mono">
                API : {health.releaseName} (v{health.version || '0.0.1'})
              </span>
            )}
          </div>
        </div>

        {/* Bouton d'actualisation forcée */}
        <Button
          type="button"
          onClick={handleForceReload}
          disabled={isReloading}
          variant="outline"
          className="w-full mt-1 border-emerald-600/50 hover:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold flex items-center justify-center gap-2 h-11"
        >
          <RefreshCw className={`w-4 h-4 ${isReloading ? 'animate-spin' : ''}`} />
          {isReloading
            ? 'Téléchargement de la dernière version...'
            : 'Forcer l’actualisation (Vider le cache & Recharger)'}
        </Button>
      </CardContent>
    </Card>
  );
}
